import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BackgroundImageUploader } from '@/components/BackgroundImageUploader';
import { useBackgroundImage } from '@/hooks/useBackgroundImage';
import heroImage from '@/assets/triathlon-finish-line.jpg';

interface HeroSectionProps {
  user: any;
  isAdmin: boolean;
  isAdminMode: boolean;
  onViewSampleAndTemplates: () => void;
  onViewChecklists: () => void;
  hasUserChecklists: () => boolean;
  handleAddSection: () => void;
}

export const HeroSection = ({
  user,
  isAdmin,
  isAdminMode,
  onViewSampleAndTemplates,
  onViewChecklists,
  hasUserChecklists,
  handleAddSection
}: HeroSectionProps) => {
  const navigate = useNavigate();
  // Skip loading background for non-admin users (saves 300-400ms on mobile)
  const { backgroundImage } = useBackgroundImage('home', false, !isAdminMode);

  return (
    <section className="relative min-h-[50vh] sm:min-h-[60vh] md:h-[65vh] lg:h-[70vh] overflow-hidden pt-20 sm:pt-16 md:pt-12">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={backgroundImage?.image_url || heroImage}
          alt="Triathlon athlete preparing for race day"
          className="w-full h-full"
          data-hero-image="true"
          style={{
            objectFit: 'cover',
            objectPosition: backgroundImage?.background_position || 'center center'
          }}
          loading="eager"
          fetchPriority="high"
        />
        {/* Bold blue overlay - EVELYN palette */}
        <div className="absolute inset-0 bg-primary/60" />
        {/* Gradient fade into next section */}
        <div className="absolute bottom-0 left-0 right-0 h-20 sm:h-28 bg-gradient-to-b from-transparent to-background pointer-events-none" />
      </div>

      {/* Hero Content - Bottom Left */}
      <div className="relative h-full flex items-end pb-8 md:pb-20 px-4 md:px-16 max-w-[1280px] mx-auto">
        <div className="max-w-[640px]">
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-semibold text-white mb-8 sm:mb-10 leading-tight tracking-tight"
          >
            Train Hard. <br />Pack Smart. <br />Race Confident.
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/95 mb-6 md:mb-8 font-body max-w-[500px]">
            Build your custom triathlon checklist in minutes
          </p>
          <p className="text-sm sm:text-base md:text-lg text-white/85 mb-10 md:mb-12 font-body max-w-[500px]">
            Create custom checklists, track your progress, and ensure you're race-day ready
          </p>
          
          {/* CTA Button */}
          <Button 
            size="lg" 
            onClick={handleAddSection}
            variant="accent"
            className="w-full sm:w-auto h-11 sm:h-12 md:h-14 px-5 sm:px-6 md:px-8 text-base md:text-lg text-white rounded-sm font-normal"
          >
            Create Your Checklist
          </Button>
        </div>
      </div>

      {/* Admin Image Upload (preserve existing functionality) */}
      {isAdmin && isAdminMode && (
        <div className="absolute top-4 right-4 z-10">
          <BackgroundImageUploader />
        </div>
      )}
    </section>
  );
};
